import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Newspaper, ArrowSquareOut, TrendUp, TrendDown, Minus, ArrowsClockwise, Funnel, Clock, Lightning, Spinner, Tag
} from "@phosphor-icons/react";

const ASSET_FILTERS = ["ALL", "BTC", "ETH", "BNB", "SOL", "SPX", "NVDA", "DXY", "TNX", "CRYPTO"];

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function SentimentBadge({ sentiment }) {
  if (!sentiment) return null;
  const { label, score } = sentiment;
  if (label === "bullish") {
    return (
      <Badge className="text-[8px] px-1.5 py-0 h-4 border-0 rounded-sm bg-emerald-900/50 text-emerald-400 gap-0.5" data-testid="sentiment-bullish">
        <TrendUp size={8} weight="bold" /> BULLISH {score > 50 ? `+${score}` : ""}
      </Badge>
    );
  }
  if (label === "bearish") {
    return (
      <Badge className="text-[8px] px-1.5 py-0 h-4 border-0 rounded-sm bg-red-900/50 text-red-400 gap-0.5" data-testid="sentiment-bearish">
        <TrendDown size={8} weight="bold" /> BEARISH {score < -50 ? score : ""}
      </Badge>
    );
  }
  return null;
}

function ImpactDot({ signal }) {
  if (signal === "high_impact") return <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" title="High impact" />;
  if (signal === "moderate") return <span className="w-1.5 h-1.5 rounded-full bg-amber-700 shrink-0" title="Moderate impact" />;
  return <span className="w-1.5 h-1.5 rounded-full bg-zinc-700 shrink-0" title="Low impact" />;
}

function NewsItem({ item, index }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`news-item-${index}`}
      className="group block border border-amber-500/25 rounded-sm my-1.5 py-3 px-3 hover:bg-zinc-900/40 hover:border-amber-500/40 transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className="mt-1.5 shrink-0">
          <ImpactDot signal={item.pattern_signal} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <h3 className="text-[13px] font-medium text-zinc-200 leading-snug group-hover:text-amber-300 transition-colors flex-1 min-w-0 break-words">
              {item.title}
            </h3>
            <ArrowSquareOut size={12} className="text-zinc-700 group-hover:text-zinc-400 transition-colors shrink-0 mt-0.5" />
          </div>
          {item.description && (
            <p className="text-[11px] text-zinc-500 mt-1 line-clamp-2 leading-relaxed">{item.description}</p>
          )}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className="text-[10px] text-zinc-600 flex items-center gap-1">
              <Clock size={9} />
              {timeAgo(item.published_at)}
            </span>
            <span className="text-[10px] text-zinc-600">{item.source}</span>
            {item.source_type === "x_post" && item.verified && (
              <Badge className="text-[7px] px-1 py-0 h-3.5 border-0 rounded-sm bg-blue-900/40 text-blue-400">X POST</Badge>
            )}
            <SentimentBadge sentiment={item.sentiment} />
            {item.asset_mentions?.length > 0 && (
              <div className="flex items-center gap-1">
                {item.asset_mentions.map(m => (
                  <Badge key={m} className="text-[7px] px-1 py-0 h-3.5 border-0 rounded-sm bg-zinc-800/80 text-zinc-400">
                    {m}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        {item.image_url && (
          <img
            src={item.image_url}
            alt=""
            className="w-20 h-14 object-cover rounded-sm shrink-0 hidden sm:block opacity-60 group-hover:opacity-90 transition-opacity"
            loading="lazy"
            onError={(e) => e.target.style.display = "none"}
          />
        )}
      </div>
    </a>
  );
}

export default function LiveNews() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const [impactFilter, setImpactFilter] = useState("all");

  const fetchNews = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const assetParam = filter !== "ALL" ? `&asset=${filter}` : "";
      const resp = await axios.get(`${API}/live-news?limit=80${assetParam}`);
      setData(resp.data);
    } catch (e) {
      console.error("Failed to fetch live news:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchNews();
    const interval = setInterval(() => fetchNews(true), 60000);
    return () => clearInterval(interval);
  }, [fetchNews]);

  const items = data?.items || [];
  const filtered = impactFilter === "all" ? items :
    impactFilter === "high" ? items.filter(i => i.pattern_signal === "high_impact") :
    impactFilter === "sentiment" ? items.filter(i => i.sentiment?.label !== "neutral") :
    items;

  const bullishCount = items.filter(i => i.sentiment?.label === "bullish").length;
  const bearishCount = items.filter(i => i.sentiment?.label === "bearish").length;
  const highImpact = items.filter(i => i.pattern_signal === "high_impact").length;

  return (
    <div className="p-4 sm:p-5 space-y-4 max-w-[1200px] overflow-x-hidden" data-testid="live-news-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-zinc-50 tracking-tight flex items-center gap-2">
            <Newspaper size={24} weight="bold" className="text-amber-400" />
            Live News Feed
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Real-time market news with sentiment analysis and trader pattern signals
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {data?.fetched_at && (
            <span className="text-[10px] text-zinc-600 font-data">
              Updated {new Date(data.fetched_at).toLocaleTimeString()}
            </span>
          )}
          <button
            data-testid="refresh-news-btn"
            onClick={() => fetchNews(true)}
            disabled={refreshing}
            className="text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2 py-1 border border-zinc-800 rounded-sm flex items-center gap-1.5"
          >
            {refreshing ? <Spinner size={11} className="animate-spin" /> : <ArrowsClockwise size={11} />}
            Refresh
          </button>
        </div>
      </div>

      {/* Sentiment Summary */}
      {!loading && items.length > 0 && (
        <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap text-[10px] border border-zinc-800/40 rounded-sm px-3 py-2 bg-zinc-900/20" data-testid="sentiment-summary">
          <span className="text-zinc-500 font-medium">MARKET PULSE</span>
          <span className="flex items-center gap-1 text-emerald-400">
            <TrendUp size={10} weight="bold" /> {bullishCount} bullish
          </span>
          <span className="flex items-center gap-1 text-red-400">
            <TrendDown size={10} weight="bold" /> {bearishCount} bearish
          </span>
          <span className="flex items-center gap-1 text-amber-400">
            <Lightning size={10} weight="fill" /> {highImpact} high impact
          </span>
          <span className="text-zinc-600">{items.length} total stories</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:flex-wrap">
        {/* Asset filter */}
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin pb-1 sm:pb-0 sm:flex-wrap -mx-1 px-1" data-testid="asset-filters">
          <Tag size={10} className="text-zinc-600 mr-1 shrink-0" />
          {ASSET_FILTERS.map(af => (
            <button
              key={af}
              onClick={() => setFilter(af)}
              data-testid={`filter-${af.toLowerCase()}`}
              className={`text-[9px] font-medium px-2 py-1 rounded-sm border transition-colors shrink-0 ${
                filter === af
                  ? "bg-zinc-700/60 border-zinc-600 text-zinc-200"
                  : "bg-transparent border-zinc-800/60 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700"
              }`}
            >
              {af}
            </button>
          ))}
        </div>

        {/* Impact filter */}
        <div className="flex items-center gap-1 sm:ml-auto overflow-x-auto scrollbar-thin pb-1 sm:pb-0 sm:flex-wrap -mx-1 px-1">
          <Funnel size={10} className="text-zinc-600 mr-1 shrink-0" />
          {[
            { key: "all", label: "All" },
            { key: "high", label: "High Impact" },
            { key: "sentiment", label: "Has Sentiment" },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setImpactFilter(f.key)}
              data-testid={`impact-${f.key}`}
              className={`text-[9px] font-medium px-2 py-1 rounded-sm border transition-colors shrink-0 whitespace-nowrap ${
                impactFilter === f.key
                  ? "bg-zinc-700/60 border-zinc-600 text-zinc-200"
                  : "bg-transparent border-zinc-800/60 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* News list */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-20 bg-zinc-900/60" />
          ))}
        </div>
      ) : filtered.length > 0 ? (
        <div className="space-y-0" data-testid="news-list">
          {filtered.map((item, i) => (
            <NewsItem key={i} item={item} index={i} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-zinc-600">
          <Newspaper size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No news matching your filters</p>
        </div>
      )}
    </div>
  );
}
